import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Calculator, Save, CheckCircle2, Palmtree, UtensilsCrossed, Wheat, FileDown, Sheet, Share2 } from "lucide-react";
import api, { getErrorMessage } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { useBuilderLang } from "@/lib/builderStrings";
import { dayName } from "@/lib/customCalc";
import { isoDate, formatDateDDMMYYYY as fmtDate } from "@/lib/format";
import { downloadUrl, shareUrlWhatsApp } from "@/lib/download";
import CustomResultDisplay from "@/components/CustomResultDisplay";
import { StudentInputs } from "@/components/builder/StepPreview";

const HOLIDAY_REASONS = ["Government Holiday", "Sunday", "Local Holiday", "School Closed", "Other"];
const Toggle = ({ on, onClick, children, testid, dark }) => (
  <button onClick={onClick} data-testid={testid}
    className={`min-h-[52px] rounded-xl font-bold flex items-center justify-center gap-2 border-2 transition-colors ${on ? (dark ? "bg-thali-ink text-white border-thali-ink" : "bg-thali-green text-white border-thali-green") : "bg-white text-thali-ink/70 border-thali-turmeric/30"}`}>
    {children}
  </button>
);

export default function CustomEntry({ calcId, initialDate }) {
  const { t } = useLang();
  const { b, bl } = useBuilderLang();
  const { hasAccess, openUpgrade, handle402 } = usePlan();
  const { user } = useAuth();
  const [date, setDate] = useState(initialDate || isoDate(new Date()));
  const [day, setDay] = useState(null);
  const [counts, setCounts] = useState({});
  const [dayType, setDayType] = useState("mdm");
  const [payMode, setPayMode] = useState("dbt");
  const [reason, setReason] = useState("Government Holiday");
  const [result, setResult] = useState(null);
  const [savedSig, setSavedSig] = useState(null);
  const [trial, setTrial] = useState(user?.trial || null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const pending = useRef(false);
  const isFuture = date > isoDate(new Date());
  const sig = `${date}|${dayType}|${payMode}|${JSON.stringify(counts)}`;
  const dirty = savedSig !== sig;

  const load = async (d) => {
    setResult(null); setErr(""); setSavedSig(null); setDay(null);
    try {
      const { data } = await api.get(`/calculators/${calcId}/day`, { params: { d } });
      setDay(data);
      try {
        const { data: ed } = await api.get(`/calculators/${calcId}/entries/${d}`);
        const e = ed.entry;
        setCounts(e.students); setDayType(e.day_type); setPayMode(e.payment_mode || "dbt"); setResult(e.result);
        setSavedSig(`${d}|${e.day_type}|${e.payment_mode || "dbt"}|${JSON.stringify(e.students)}`);
      } catch {
        setDayType(data.day.serving_day ? "mdm" : "holiday");
        const { data: pd } = await api.get("/profile").catch(() => ({ data: {} }));
        const en = pd.profile?.enrolled || {};
        setCounts(Object.fromEntries(data.day.classes.map((c) => [c.key, parseInt(en[c.key] || 0, 10) || 0])));
      }
    } catch (e) { setErr(getErrorMessage(e)); }
  };
  useEffect(() => {
    load(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcId]);

  const total = Object.values(counts).reduce((a, v) => a + (parseInt(v, 10) || 0), 0);
  const calculate = async () => {
    setErr("");
    if (total === 0) { setErr("Please enter at least one student."); return; }
    setBusy("calc");
    try {
      const { data } = await api.post(`/calculators/${calcId}/calc`, { date, students: counts, day_type: dayType });
      setResult(data.result);
      if (!data.preview) setTrial((p) => ({ ...(p || {}), enabled: true, limit: data.limit, remaining: data.remaining, unlimited: data.unlimited }));
    } catch (e) {
      if (e?.response?.status === 402) { pending.current = true; setResult(null); setTrial((p) => ({ ...(p || {}), remaining: 0 })); }
      else setErr(getErrorMessage(e));
    } finally { setBusy(""); }
  };
  useEffect(() => {
    if (hasAccess && pending.current) { pending.current = false; calculate(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  const save = async (holiday) => {
    if (isFuture) { toast.error(t("future_preview")); return false; }
    setBusy("save");
    const body = holiday
      ? { date, students: {}, day_type: "holiday", holiday_reason: reason }
      : { date, students: counts, day_type: dayType, payment_mode: dayType === "dry_ration" ? payMode : undefined };
    try {
      const { data } = await api.post(`/calculators/${calcId}/entries`, body);
      setResult(data.entry.result);
      setSavedSig(`${date}|${data.entry.day_type}|${payMode}|${JSON.stringify(data.entry.students)}`);
      toast.success(holiday ? "Marked as holiday" : "Entry saved!");
      return true;
    } catch (e) { if (!handle402(e)) toast.error(getErrorMessage(e)); return false; }
    finally { setBusy(""); }
  };

  const exportPath = `/calculators/${calcId}/entries/${date}/export`;
  const download = async (fmt) => {
    if (!hasAccess) { openUpgrade(); return; }
    if (dirty && !(await save(false))) return;
    setBusy(fmt);
    try { await downloadUrl(exportPath, { fmt }, `MDM_Daily_${fmtDate(date)}.${fmt === "excel" ? "xlsx" : "pdf"}`); toast.success("Downloaded"); }
    catch (e) { if (!handle402(e)) toast.error(getErrorMessage(e)); }
    finally { setBusy(""); }
  };
  const share = async () => {
    if (dirty && !(await save(false))) return;
    setBusy("wa");
    try { await shareUrlWhatsApp(exportPath, {}, `MDM entry ${fmtDate(date)}`, `MDM_Daily_${fmtDate(date)}.pdf`); }
    finally { setBusy(""); }
  };

  const di = day?.day;
  return (
    <div className="space-y-5" data-testid="custom-entry">
      <div className="card-thali space-y-4">
        <input type="date" className="input-thali" value={date} onChange={(e) => { setDate(e.target.value); load(e.target.value); }} data-testid="centry-date" />
        {di && (
          <div className="rounded-2xl p-4 bg-gradient-to-br from-thali-cream to-white border border-thali-turmeric/30 animate-fade-in-up" data-testid="centry-day-card">
            <div className="flex items-center justify-between">
              <div className="font-heading font-extrabold text-lg">{dayName(di.weekday_number, bl)}</div>
              <div className="text-sm font-num text-thali-ink/60">{fmtDate(date)}</div>
            </div>
            <div className="text-sm mt-1 text-thali-ink/80" data-testid="centry-meal">🍽️ {di.serving_day ? (di.menu_display || "—") : b("day_off")}</div>
          </div>
        )}
        {isFuture && <div className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="centry-future">⏳ {t("future_preview")}</div>}
        <div className="grid grid-cols-2 gap-2">
          <Toggle on={dayType !== "holiday"} onClick={() => { setDayType("mdm"); setResult(null); }} testid="centry-working"><CheckCircle2 className="w-5 h-5" /> {t("working_day")}</Toggle>
          <Toggle dark on={dayType === "holiday"} onClick={() => { setDayType("holiday"); setResult(null); }} testid="centry-holiday"><Palmtree className="w-5 h-5" /> {t("holiday")}</Toggle>
        </div>
        {dayType === "holiday" ? (
          <div className="rounded-2xl bg-thali-ink/5 border border-thali-ink/15 p-4 space-y-3" data-testid="centry-holiday-panel">
            <select className="input-thali" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="centry-holiday-reason">
              {HOLIDAY_REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
            <button onClick={() => save(true)} disabled={!!busy || isFuture} className="btn-3d-green w-full" data-testid="centry-mark-holiday"><Save className="w-4 h-4" /> {t("mark_holiday")}</button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Toggle on={dayType === "mdm"} onClick={() => { setDayType("mdm"); setResult(null); }} testid="centry-mdm"><UtensilsCrossed className="w-4 h-4" /> {t("mdm_serving")}</Toggle>
              <Toggle on={dayType === "dry_ration"} onClick={() => { setDayType("dry_ration"); setResult(null); }} testid="centry-dry"><Wheat className="w-4 h-4" /> {t("dry_ration_day")}</Toggle>
            </div>
            {dayType === "dry_ration" && (
              <div className="flex gap-2">
                {["dbt", "cash"].map((m) => <Toggle key={m} on={payMode === m} onClick={() => setPayMode(m)} testid={`centry-pay-${m}`}>{m === "dbt" ? t("bank_dbt") : t("cash_hand")}</Toggle>)}
              </div>
            )}
            {di && <StudentInputs classes={di.classes} counts={counts} setCounts={(f) => { setCounts(f); setResult(null); }} prefix="centry" />}
            {err && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-2" data-testid="centry-error">{err}</div>}
            {trial && trial.enabled && !trial.unlimited && !hasAccess && trial.remaining != null && (
              <div className="text-center" data-testid="centry-free-chip"><span className="text-xs font-bold bg-thali-green/15 text-thali-green rounded-full px-3 py-1">🎁 {trial.remaining} of {trial.limit} {t("free_calcs_left")}</span></div>
            )}
            <button onClick={calculate} disabled={!!busy} className="btn-3d-saffron w-full text-lg" data-testid="centry-calculate"><Calculator className="w-5 h-5" /> {busy === "calc" ? "…" : t("calculate")}</button>
          </>
        )}
      </div>
      {result && dayType !== "holiday" && (
        <>
          <CustomResultDisplay result={result} b={b} testid="centry-result" />
          <button onClick={() => save(false)} disabled={!!busy || isFuture} className="btn-3d-green w-full text-lg" data-testid="centry-save"><Save className="w-5 h-5" /> {busy === "save" ? "…" : t("save_entry")}</button>
          {savedSig && !isFuture && (
            <div className="flex gap-2" data-testid="centry-download-bar">
              <button onClick={() => download("pdf")} disabled={!!busy} className="btn-3d-saffron flex-1 text-sm" data-testid="centry-dl-pdf"><FileDown className="w-4 h-4" /> {busy === "pdf" ? "…" : t("download_pdf")}</button>
              <button onClick={() => download("excel")} disabled={!!busy} className="btn-3d-green flex-1 text-sm" data-testid="centry-dl-excel"><Sheet className="w-4 h-4" /> {busy === "excel" ? "…" : t("download_excel")}</button>
              {hasAccess && <button onClick={share} disabled={!!busy} className="btn-ghost px-3" data-testid="centry-share"><Share2 className="w-4 h-4" /></button>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
